import { lazy, Suspense, useState } from "react";
import type { Report } from "../../lib/api/index.js";
import { useReportRange } from "../../lib/report-range.js";
import type { ScreenKind } from "../../lib/routes.js";
import { Download } from "../icons.js";
import "./export-dialog.css";

const ExportFlow = lazy(() =>
  import("./export-flow.js").then((module) => ({ default: module.ExportFlow })),
);

const PublishExport = lazy(() =>
  import("../../screens/publish/export-sheet.js").then((module) => ({
    default: module.PublishExport,
  })),
);

/** What Export makes on each screen that has one; every other screen has none. */
export type ExportKind = "overview" | "publish";

export function exportKind(screen: ScreenKind): ExportKind | null {
  if (screen === "overview") return "overview";
  if (screen === "offPage") return "publish";
  return null;
}

/**
 * "Export" in the top bar (DESIGN.md §5), on the Overview and on Where to
 * publish only: the Overview as a PDF, Where to publish as an Excel workbook.
 * It exports the report on screen: choose another in the rail and this follows.
 *
 * Only the button is in the app's first download; each popup, and the
 * Overview's printed pages, arrive on the first click.
 */
export function ExportButton({
  kind,
  workspaceId,
  report,
  today,
}: {
  readonly kind: ExportKind;
  readonly workspaceId: string;
  readonly report: Report;
  readonly today: Date;
}) {
  const { engine, today: todayKey } = useReportRange(today);
  const [open, setOpen] = useState(false);
  const end = (): void => {
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className="button-secondary export-button"
        disabled={open}
        onClick={() => {
          setOpen(true);
        }}
      >
        <Download aria-hidden="true" />
        Export
      </button>
      {open ? (
        <Suspense fallback={null}>
          {kind === "overview" ? (
            <ExportFlow
              workspaceId={workspaceId}
              report={report}
              engine={engine}
              today={todayKey}
              onEnd={end}
            />
          ) : (
            <PublishExport
              workspaceId={workspaceId}
              report={report}
              today={today}
              onClose={end}
            />
          )}
        </Suspense>
      ) : null}
    </>
  );
}
